/**
 * MTEXT's CACHED LAYOUT MEASUREMENT (DXF groups 42/43) and when an edit
 * INVALIDATES it — @codex PR #794 round 14 P2 (the ENTITY write path) and
 * round 15 P2 (the STYLE TABLE one — see the second half of this module).
 *
 * # What groups 42/43 are
 *
 * `horizontalWidth` (group 42) and `verticalHeight` (group 43) are NOT inputs.
 * The Autodesk DXF reference marks them "horizontal width of the characters
 * that make up the mtext entity" / "vertical height of the mtext entity" —
 * AutoCAD MEASURES the laid-out block with its own font metrics and WRITES the
 * result; `ezdxf` documents the same pair as read-only values it does not
 * maintain, and neither ezdxf nor this repo requires them (the writer emits 42
 * and 43 only when non-zero, and an MTEXT without them is ordinary valid DXF).
 * AutoCAD RECOMPUTES them whenever the laid-out block changes.
 *
 * # Why the write path must clear them
 *
 * The live Properties path owns MTEXT `contents`, `nominalHeight` (Height) and
 * `referenceWidth` (Width) as plain typed leaves. Before this module it patched
 * those and left the IMPORTED measurement untouched, so an edited MTEXT carried
 * AutoCAD's measurement of a string it no longer holds. Two consumers read it:
 *
 *   1. {@link mtextBoxSize}'s SEMANTIC rectangle takes group 42 VERBATIM as its
 *      width, so an edited MTEXT has had a wrong semantic box for as long as the
 *      Properties path has been able to edit one (a pre-existing defect);
 *   2. the EXACT select ring's mesh right edge is `min(walkedRight, group 42)`
 *      (round 13 — a worst-case walk may not become exact crossing geometry).
 *      That clamp's PRECONDITION is that group 42 describes the CURRENT layout;
 *      against a stale one it truncates the ring to the OLD width while the
 *      renderer paints the NEW string, and a crossing window over the visible
 *      suffix MISSES the entity — the round-13 defect mirrored, same axis.
 *
 * Fixing it at the source repairs (1) as well as closing (2), which is why the
 * clearing lives HERE — in the authoritative write contour (dispatch → reducer
 * → `EntityMutation` → `commitMutation`) — and not in the bounds module, which
 * is a DERIVED reader and cannot tell a fresh measurement from a stale one.
 *
 * # Stated as a PREV→NEXT diff, not as "which labels were submitted"
 *
 * {@link mtextLayoutMeasurementInvalidation} compares the pre-mutation entity
 * with the one the apply produced. That is deliberate: a `properties/apply`
 * reaches the MTEXT through MORE THAN ONE builder. The typed writer owns
 * Contents / Text style / Height / Width, but `Line spacing`
 * (`lineSpacingFactor`) is cutover-DEFERRED and still lands through the legacy
 * `propertyTranslator` map — so a rule keyed on the typed writer's own patch
 * would have left exactly that edit stale. A diff over the FIELDS cannot miss
 * a builder, present or future, and it is also immune to a submitted label
 * that the writer REJECTED or no-opped (no field changed ⇒ no invalidation).
 *
 * Clearing to 0 (the schema default, and the "no group 42/43" state the writer
 * omits) is the faithful action: this repo cannot reproduce AutoCAD's own font
 * measurement, and 0 is exactly how every MTEXT that never carried one is
 * already handled — `mtextBoxSize` falls back to the contents walk, the export
 * emits no 42/43, and AutoCAD recomputes on load. Fabricating a REPLACEMENT
 * measurement from the bundled atlases would put this renderer's reach into a
 * field that is defined as AutoCAD's, which is the wrong data in the other
 * direction.
 *
 * # Both halves of the pair, always
 *
 * Any layout-affecting change invalidates BOTH 42 and 43, never one: they are
 * two numbers describing ONE laid-out block, AutoCAD recomputes the block (not
 * a single axis), and a "width-only" edit does not exist — a wider wrap column
 * re-flows the lines and changes the height too.
 *
 * # TWO write paths, ONE classification (round 15 P2)
 *
 * An MTEXT's laid-out block is a function of the ENTITY's own fields AND of the
 * TEXT STYLE its `styleName` resolves to. Editing the style changes no entity
 * field at all, so the prev→next entity diff above cannot see it — the second
 * half of this module classifies `textStyleSchema` the same way and exposes the
 * style-keyed sweep the style reducer applies. Both classifications live HERE so
 * "does this change the layout" has one home; a copy of either list inside a
 * reducer is how round 14 and round 15 became two rounds instead of one.
 */

import type {
  BlockChildEntity,
  DrawingEntity,
  MtextEntity,
  TextStyle,
} from "@dxf-ai-editor/drawing-model";

/**
 * The two cached-measurement fields, in one place so a reader never has to
 * rediscover that 42 and 43 travel together.
 */
export const MTEXT_LAYOUT_MEASUREMENT_FIELDS = [
  "horizontalWidth",
  "verticalHeight",
] as const;

/**
 * EVERY field of `mtextEntitySchema` (common base included), classified: does
 * changing it change the laid-out block AutoCAD measured into groups 42/43?
 *
 * EXHAUSTIVE BY CONSTRUCTION — the mapped type over `keyof MtextEntity` makes a
 * new schema field a COMPILE error until it is classified here, and
 * `mtextLayoutMeasurement.test.ts` additionally compares the key set against
 * `mtextEntitySchema.shape` at runtime (so a field added to the schema without
 * touching the type — or a key left here after a schema field is removed — is
 * caught too). Enumerating rather than listing the few fields that happen to be
 * writable today is deliberate: the writable set grows (MText `Line spacing`,
 * `Attachment` and `Direction` are cutover-DEFERRED, not absent), and a
 * hand-picked list would silently miss the day one of them lands.
 *
 * TRUE — the field feeds the text layout:
 *   - `contents` (1/3), `styleName` (7 — the font, and the style's own height /
 *     width factor, are the glyph metrics), `nominalHeight` (40),
 *     `referenceWidth` (41 — the WRAP column: it decides where lines break, so
 *     it changes both the measured width and the line count),
 *     `lineSpacingStyle` (73) and `lineSpacingFactor` (44) — the line grid;
 *   - `drawingDirection` (72) — left-to-right vs top-to-bottom flow lays the
 *     same runs out into a different block;
 *   - the COLUMN set (`columnType` 75, `columnCount` 76, `columnWidth` 48,
 *     `columnGutter` 49, `columnHeights` 50, `columnFlowReversed`,
 *     `columnAutoheight`) — columns re-flow the whole block.
 *
 * FALSE — the field places, paints or annotates the block without resizing it:
 *   - `insertionPoint` (10), `attachmentPoint` (71), `rotation` (50),
 *     `xAxisDirection` (11) and `extrusion` (210): WHERE and at what angle the
 *     measured block sits. AutoCAD measures the block in its own upright frame;
 *     42/43 are lengths, invariant under placement.
 *   - the BACKGROUND-fill set (`backgroundFill`, `backgroundFillSetting`,
 *     `backgroundFillColor`, `backgroundFillColorACI`, `backgroundFillColorRGB`,
 *     `backgroundTransparency`, `fillBoxScale`): the fill box is DERIVED from
 *     the text extents (`fillBoxScale` × them) — it is painted around the
 *     measurement, it does not change it.
 *   - `annotativeContexts` / `creationScaleName`: per-scale context snapshots.
 *     A context carries its OWN scaled geometry; the master 42/43 stay the
 *     master block's measurement.
 *   - `type`, `id`, `spaceId` and the common base (layer / colour / linetype /
 *     lineweight / transparency / linetype scale / the array-membership,
 *     handle, material, plot-style, visibility, shadow, extension-dictionary
 *     and raw-group channels): identity, appearance and bookkeeping. None of
 *     them reaches the glyph run.
 *
 * NOTE `widthFactor` is NOT in this map because MTEXT has no such field — it is
 * TEXT's group 41. An MTEXT's per-run width scaling arrives INSIDE `contents`
 * as an inline `\W` code, so it is already covered by `contents`.
 *
 * MUTATION-TESTING NOTE: this is a module-scope initializer, so every mutant
 * inside it (one per boolean, plus the literal and the derived-set chain
 * below) is a Stryker STATIC mutant, and the vitest runner cannot activate one
 * — the module is imported, and the table evaluated, before the mutant is
 * switched on. Those survivors are a runner limitation, not a coverage gap:
 * `mtextLayoutMeasurement.test.ts` pins the key set against
 * `mtextEntitySchema.shape` AND the true half against an explicit list, so any
 * flipped value, an added/removed key or an emptied table fails the suite
 * (verified by applying each mutant class to this source by hand).
 */
export const MTEXT_FIELD_AFFECTS_LAYOUT: {
  readonly [K in keyof MtextEntity]-?: boolean;
} = {
  // ── the laid-out block ──
  contents: true,
  styleName: true,
  nominalHeight: true,
  referenceWidth: true,
  lineSpacingStyle: true,
  lineSpacingFactor: true,
  drawingDirection: true,
  columnType: true,
  columnCount: true,
  columnWidth: true,
  columnGutter: true,
  columnHeights: true,
  columnFlowReversed: true,
  columnAutoheight: true,
  // ── placement / orientation ──
  insertionPoint: false,
  attachmentPoint: false,
  rotation: false,
  xAxisDirection: false,
  extrusion: false,
  // ── the measurement itself (clearing it must not re-trigger the clear) ──
  horizontalWidth: false,
  verticalHeight: false,
  // ── background fill: derived from the extents, never an input to them ──
  backgroundFill: false,
  backgroundFillSetting: false,
  backgroundFillColor: false,
  backgroundFillColorACI: false,
  backgroundFillColorRGB: false,
  backgroundTransparency: false,
  fillBoxScale: false,
  // ── annotative contexts (each carries its own geometry) ──
  annotativeContexts: false,
  creationScaleName: false,
  // ── identity + the common entity base ──
  type: false,
  id: false,
  spaceId: false,
  layer: false,
  color: false,
  linetype: false,
  lineweight: false,
  transparency: false,
  linetypeScale: false,
  arrayGroupId: false,
  arraySourceOfGroup: false,
  arrayMode: false,
  ownerHandle: false,
  materialHandle: false,
  plotStyleHandle: false,
  visibility: false,
  shadowMode: false,
  extensionDictionaryHandle: false,
  extraGroups: false,
};

/** The layout-affecting field NAMES — {@link MTEXT_FIELD_AFFECTS_LAYOUT}'s true
 *  half, as the set the write path membership-tests a patch's keys against. */
export const MTEXT_LAYOUT_AFFECTING_FIELDS: ReadonlySet<string> = new Set(
  Object.entries(MTEXT_FIELD_AFFECTS_LAYOUT)
    .filter(([, affects]) => affects)
    .map(([field]) => field),
);

/** Does `field` feed the laid-out block groups 42/43 measure? (Unknown names —
 *  a nested path, a label — are NOT layout-affecting: this answers about the
 *  top-level schema fields a typed patch carries.) */
export function mtextFieldAffectsLayout(field: string): boolean {
  return MTEXT_LAYOUT_AFFECTING_FIELDS.has(field);
}

/**
 * Equality for a classified field value. Every layout-affecting MTEXT field is
 * a primitive except `columnHeights` (a number array), and the entities this
 * rule compares have been through `patchEntityById` +
 * `assembleDrawingDocumentInPlace`, which hands back a FRESH array holding the
 * same numbers — so identity alone would read an untouched column list as an
 * edit and throw the measurement away on every apply. The structural fallback
 * is therefore load-bearing, not defensive; it is a plain `||` rather than a
 * typeof-guarded branch because for these field values the guard changes no
 * answer (two differing primitives serialize differently, and `undefined` on
 * one side only is already unequal either way) — an untestable branch is worse
 * than no branch.
 */
function fieldValueEqual(a: unknown, b: unknown): boolean {
  return Object.is(a, b) || JSON.stringify(a) === JSON.stringify(b);
}

/**
 * The 42/43 CLEAR the reducer folds into an MTEXT apply, or `null` when there
 * is nothing to invalidate — `next` is not an MTEXT, the apply changed no
 * layout-affecting field, or the entity carries no measurement (both already
 * 0, the state of every MTEXT this editor created and of every imported one
 * whose file omitted the groups).
 *
 * `prev` is the PRE-mutation document entity and `next` the entity after every
 * builder in the same apply has run (the typed writer's plain + side-effect
 * steps and the legacy translator). Comparing them — rather than inspecting
 * the submitted labels or one builder's patch — is what makes this complete
 * across write surfaces; see the module header.
 */
export function mtextLayoutMeasurementInvalidation(
  prev: DrawingEntity,
  next: DrawingEntity,
): { horizontalWidth: number; verticalHeight: number } | null {
  if (prev.type !== "MText" || next.type !== "MText") return null;
  const before = prev as MtextEntity;
  const after = next as MtextEntity;
  // Nothing measured ⇒ nothing to invalidate. Checked on the POST state so a
  // builder that already wrote the pair (none does today) is not re-cleared.
  if (after.horizontalWidth === 0 && after.verticalHeight === 0) return null;
  for (const field of MTEXT_LAYOUT_AFFECTING_FIELDS) {
    if (
      !fieldValueEqual(
        (before as unknown as Record<string, unknown>)[field],
        (after as unknown as Record<string, unknown>)[field],
      )
    ) {
      return { horizontalWidth: 0, verticalHeight: 0 };
    }
  }
  return null;
}

/**
 * Did an edit DROP an MTEXT's cached measurement (a non-zero 42 or 43 became a
 * different value)? The `properties/apply` UNDO inverse is a display-STRING
 * replay of the labels the apply changed, and groups 42/43 have no label at all
 * — replaying `Contents` re-runs the forward clear, so the original measurement
 * would be LOST, which makes the edit lossy rather than undoable. `editorStore`
 * consults this to compose the pre-edit TYPED SNAPSHOT into the inverse instead
 * (the same mechanism the presence-bit and viewport-coupled sweeps use).
 *
 * Kept separate from {@link mtextLayoutMeasurementInvalidation} rather than
 * derived from it: the forward rule asks "should this apply clear the pair",
 * the inverse rule asks "did the committed document lose it", and the second
 * must stay true for ANY producer of the loss, not only the first.
 *
 * Sibling of `firstGainedOptionalFieldPath` (apps/web
 * `propertiesApplyPresenceRestore.ts`), which deliberately covers only the
 * absent→present direction; this is the value-LOSS case that detector excludes.
 *
 * LOSS ONLY, symmetrically scoped: a measurement that was 0 and became
 * non-zero is NOT reported. No write path can produce that — nothing writes
 * groups 42/43 but the invalidation above (which only clears) and the DXF
 * import — so reporting it would compose a whole-entity snapshot into the
 * inverse of applies that never need one.
 */
export function mtextLayoutMeasurementDropped(
  prev: DrawingEntity,
  next: DrawingEntity,
): boolean {
  if (prev.type !== "MText" || next.type !== "MText") return false;
  const before = prev as MtextEntity;
  const after = next as MtextEntity;
  return (
    (before.horizontalWidth !== 0 &&
      before.horizontalWidth !== after.horizontalWidth) ||
    (before.verticalHeight !== 0 &&
      before.verticalHeight !== after.verticalHeight)
  );
}

/* ────────────────────────────────────────────────────────────────────────────
 * THE SECOND WRITE PATH: the STYLE TABLE (@codex PR #794 round 15 P2)
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * EVERY field of `textStyleSchema`, classified: does changing it change the
 * laid-out block AutoCAD measured into an MTEXT's groups 42/43?
 *
 * # Why a SECOND classification, and why it lives here
 *
 * Round 14 cleared 42/43 from the ENTITY write contour, keyed on a prev→next
 * diff of the MTEXT's own fields. The Text Style editor
 * (`TextStyleEditorDialog` → `updateTextStyle` → `header/update-text-style`)
 * changes a STYLE RECORD, not an entity: `styleTablesReducer.ts` rewrites
 * `styleTables.textStyles` and every MTEXT's `styleName` is untouched, so the
 * round-14 field diff sees NOTHING while the renderer resolves that unchanged
 * name through the NEW `fontName` and paints wider glyphs. The measurement
 * taken with the OLD font then (1) states a wrong SEMANTIC rectangle and (2)
 * truncates the EXACT select ring (`min(walk, group 42)`), so glyphs paint
 * outside the ring — round 13's defect reached through the style table.
 *
 * It lives in THIS module, next to {@link MTEXT_FIELD_AFFECTS_LAYOUT}, because
 * "does this change the layout" must have ONE answer: a second list inside the
 * style reducer would be a table to keep in step with this one by hand, which
 * is exactly how round 15 became a separate round from round 14.
 *
 * EXHAUSTIVE BY CONSTRUCTION — the mapped type over `keyof TextStyle` makes a
 * new schema field a COMPILE error until it is classified, and
 * `mtextLayoutMeasurement.test.ts` compares the key set against
 * `textStyleSchema.shape` at runtime.
 *
 * TRUE — the field selects or scales the glyphs, or the line layout:
 *   - `fontName` (3): the PRIMARY font file. It is also the field the Text
 *     Style editor writes and the field `sceneBulkLoadHelpers.ts::build-
 *     StyleFontMap` dispatches the renderer's atlas on, so it is the one that
 *     provably changes painted metrics today.
 *   - `fontFile` (3, the vestigial sibling) and `bigFontFile` (4, the CJK big
 *     font): both name a FONT. Vestigial today is not a classification — a
 *     writer that starts populating either changes the glyphs.
 *   - `height` (40): the style's FIXED text height. Non-zero pins the height
 *     of the text drawn in this style, which scales every advance;
 *     {@link MTEXT_FIELD_AFFECTS_LAYOUT}'s `styleName` entry already states
 *     that "the style's own height / width factor are the glyph metrics", and
 *     the two classifications must not disagree about that.
 *   - `widthFactor` (41): scales every advance, exactly as an inline `\W` does.
 *   - `obliqueAngle` (50): shears every quad, so the block's horizontal reach
 *     changes — the style-level twin of the inline `\Q` the painted-run walk
 *     already treats as an extent term.
 *   - `flags` (70): the STYLE table record's standard flags, bit 4 of which is
 *     VERTICAL text. A vertical style lays the same runs out into an entirely
 *     different block.
 *   - `fontCharset` (the TrueType descriptor's charset): selects the code page
 *     the face is resolved with, so it can change which glyphs — and which
 *     metrics — the same string paints.
 *
 * FALSE — the field is bookkeeping, or mirrors without resizing:
 *   - `name`: identity. A rename is `header/rename-*`'s business and re-points
 *     references; it does not change what the glyphs measure.
 *   - `generationFlags` (71): backward (2) and upside-down (4). Both MIRROR
 *     the run; a mirrored block has the same width and the same height, and
 *     groups 42/43 are LENGTHS. (Vertical text is `flags`, above, not here.)
 *   - `lastHeight` (42 of STYLE): the height the NEXT text created in this
 *     style gets. It is a default for future entities, not a property of any
 *     existing one.
 *   - `hadSubclassMarker`, `handle`, `ownerHandle`: DXF round-trip metadata.
 *
 * MUTATION-TESTING NOTE: module-scope initializer, so the same Stryker STATIC
 * caveat as {@link MTEXT_FIELD_AFFECTS_LAYOUT} applies — the runtime key-set
 * and true-half pins in `mtextLayoutMeasurement.test.ts` are what actually
 * hold each boolean.
 */
export const TEXT_STYLE_FIELD_AFFECTS_LAYOUT: {
  readonly [K in keyof TextStyle]-?: boolean;
} = {
  // ── the glyphs, and the grid they sit on ──
  fontName: true,
  fontFile: true,
  bigFontFile: true,
  height: true,
  widthFactor: true,
  obliqueAngle: true,
  flags: true,
  fontCharset: true,
  // ── identity, defaults and round-trip bookkeeping ──
  name: false,
  generationFlags: false,
  lastHeight: false,
  hadSubclassMarker: false,
  handle: false,
  ownerHandle: false,
};

/** The layout-affecting TEXT STYLE field NAMES — {@link
 *  TEXT_STYLE_FIELD_AFFECTS_LAYOUT}'s true half. */
export const TEXT_STYLE_LAYOUT_AFFECTING_FIELDS: ReadonlySet<string> = new Set(
  Object.entries(TEXT_STYLE_FIELD_AFFECTS_LAYOUT)
    .filter(([, affects]) => affects)
    .map(([field]) => field),
);

/** Does `field` of a STYLE record feed the laid-out block groups 42/43
 *  measure? (Unknown names are not layout-affecting — this answers about the
 *  top-level `textStyleSchema` fields a style patch carries.) */
export function textStyleFieldAffectsLayout(field: string): boolean {
  return TEXT_STYLE_LAYOUT_AFFECTING_FIELDS.has(field);
}

/**
 * Did a STYLE-TABLE edit change the laid-out block — i.e. must every MTEXT
 * drawn in this style lose its cached measurement?
 *
 * A prev→next diff over the style RECORD, for the same reason the entity rule
 * is one: the Text Style editor submits a partial patch, the reducer merges it
 * and drops `undefined` values, and a rule keyed on "which keys were submitted"
 * would fire on a same-value re-submit and miss nothing it needs to catch. The
 * value comparison is {@link fieldValueEqual}, shared with the entity rule —
 * every classified style field is a primitive, and re-using the one comparison
 * keeps the two rules from drifting on `undefined`-vs-absent.
 */
export function textStyleLayoutChanged(
  prev: TextStyle,
  next: TextStyle,
): boolean {
  for (const field of TEXT_STYLE_LAYOUT_AFFECTING_FIELDS) {
    if (
      !fieldValueEqual(
        (prev as unknown as Record<string, unknown>)[field],
        (next as unknown as Record<string, unknown>)[field],
      )
    ) {
      return true;
    }
  }
  return false;
}

/**
 * The 42/43 CLEAR a layout-affecting STYLE edit folds into one entity, or
 * `null` when there is nothing to invalidate — the entity is not an MTEXT, it
 * is not drawn in `styleName`, or it carries no measurement.
 *
 * The style-keyed counterpart of {@link mtextLayoutMeasurementInvalidation}:
 * that one answers "did THIS entity's own fields change", which is the wrong
 * question here because the entity's `styleName` does NOT change when the
 * style it names is edited. Both return the same cell, and both return it for
 * the same reason (this repo cannot reproduce AutoCAD's font measurement, and
 * 0 is the "no group 42/43" state the writer omits and AutoCAD recomputes).
 *
 * It accepts a BLOCK CHILD as well as a space entity (`blockChildEntitySchema`
 * admits MTEXT, a block-child MTEXT carries its own groups 42/43 and its own
 * `styleName`, and the block bounds path reads `mtextBoxSize` for it), so the
 * style reducer can sweep `spaces` and `blocks` with ONE predicate.
 *
 * Name matching folds case, because AutoCAD symbol-table lookups do and
 * because the RENDERER's own resolution does: `sceneBulkLoadHelpers.ts::build-
 * StyleFontMap` keys its style→font map by `name.toLowerCase()`. An MTEXT
 * whose `styleName` differs from the row only in case therefore paints with
 * the edited font, so it must lose its measurement with the rest.
 */
export function mtextStyleMeasurementInvalidation(
  entity: DrawingEntity | BlockChildEntity,
  styleName: string,
): { horizontalWidth: number; verticalHeight: number } | null {
  if (entity.type !== "MText") return null;
  const mtext = entity as MtextEntity;
  if (mtext.horizontalWidth === 0 && mtext.verticalHeight === 0) return null;
  if (mtext.styleName.toLowerCase() !== styleName.toLowerCase()) return null;
  return { horizontalWidth: 0, verticalHeight: 0 };
}
